import { Children, type ReactNode } from "react";
import { Link } from "react-router";
import { ImgForPath } from "../../../lib/images.jsx";
import { OtherFormFields } from "./ShowroomEntryParts";
import cx from "../../../lib/cx.js";
import { preLine } from "../../../lib/text.css";
import { shrink } from "../../../lib/shrink";
import { pluralize } from "../../../lib/text-utils.js";
import { Bubble } from "../../../design-system/components/Bubble/Bubble.jsx";
import Text from "../../../design-system/primitives/components/Text/Text";
import { icons } from "../../../design-system/primitives/icons";
import type { ShowroomEntryListItem } from "../../../api/showroom";
import type { WorkshopSettings } from "../../../api/workshop";
import styles from "./ShowroomEntryPreview.css";

// Only the comments toggle is read here — the card takes the narrow shape so a
// caller without a Lernreise behind it (the demo, on the spark's own defaults)
// can hand in what it has.
type PreviewSettings = Pick<WorkshopSettings, "commentsEnabled">;

// The special form fields the card reads. `formData` is untyped since its keys
// are driven by each spark's form definition.
type UploadedFile = {
  path?: string;
  data?: { path?: string; probe?: { width?: number } };
};
type PreviewFormData = {
  title?: string;
  description?: string;
  upload?: UploadedFile;
  coverImage?: UploadedFile;
};

const ReactionCount = ({ counts }: { counts: Record<string, number> }) => {
  const sum = Object.values(counts).reduce((s, c) => s + c, 0);
  if (!sum) return null;
  const Spark = icons.sparkle;
  return (
    <span className={styles.reactionCount} aria-label={`${sum} Reaktionen`}>
      <Spark size={16} aria-hidden />
      {sum}
    </span>
  );
};

/** How an entry is doing in the showroom. One that isn't there yet has none. */
type Engagement = {
  unseenCount: number;
  reactionCounts: Record<string, number>;
  commentCount: number;
  visitCounts: number;
  commentsEnabled: boolean;
};

type ShowroomEntryCardProps = {
  /** The entry's fields, in the shape the showroom serves them. */
  formData: Record<string, unknown>;
  /** The team behind the entry, named under the title. */
  memberNames?: string[];
  /** The cover image, by the path it is stored under. */
  coverPath?: string | null;
  /**
   * The cover image by URL instead, for one that isn't stored yet: the
   * showroom form previews the file the team has only just picked.
   */
  coverSrc?: string | null;
  /** Where the card leads. Without it the card is shown but not followed. */
  to?: string;
  engagement?: Engagement;
};

// A card in the showroom links to its team's full entry; the form's preview of
// a card has nowhere to lead yet.
const CardBox = ({ to, children }: { to?: string; children: ReactNode }) =>
  to ? (
    <Link to={to} className={styles.box}>
      {children}
    </Link>
  ) : (
    <div className={styles.box}>{children}</div>
  );

// No cover: nothing stands in for it — the card's own padding and gap carry
// the spacing the placeholder used to.
const CardCover = ({
  path,
  src,
}: {
  path?: string | null;
  src?: string | null;
}) => {
  if (src) return <img src={src} alt="" className={styles.cover} />;
  if (path) {
    return (
      <ImgForPath
        path={path}
        // Attributes, not a resize: they reserve the box so `loading="lazy"` works.
        width={315}
        height={192}
        className={styles.cover}
      />
    );
  }
  return null;
};

/**
 * The entry as the showroom shows it, from the pieces it is made of rather
 * than from a stored entry — so the showroom form can preview one that hasn't
 * been uploaded yet.
 */
export const ShowroomEntryCard = ({
  formData,
  memberNames = [],
  coverPath,
  coverSrc,
  to,
  engagement,
}: ShowroomEntryCardProps) => {
  const { title, description } = formData as PreviewFormData;

  return (
    <CardBox to={to}>
      {/* Inside the card, which is the positioned ancestor the bubble needs:
          as a sibling it resolved against the page shell's body instead and
          landed in the sheet's corner. The card clips to its radius, so the
          bubble is pinned inside the padding box rather than straddling the
          corner — hence its own offsets rather than Bubble's `topRight`. */}
      {engagement && engagement.unseenCount > 0 && (
        <span className={styles.unseenBubble}>
          <Bubble text={String(engagement.unseenCount)} topRight={false} />
        </span>
      )}
      <CardCover path={coverPath} src={coverSrc} />
      <div className={styles.boxBody}>
        <header className={styles.headerRow}>
          <Text variant="h6" as="h3">
            {title ?? ""}
          </Text>
          {engagement && <ReactionCount counts={engagement.reactionCounts} />}
        </header>
        {memberNames.length > 0 && (
          <Text variant="body-small">{memberNames.join(", ")}</Text>
        )}
        <OtherFormFields formData={formData} styles={styles} />

        {/* Not linkified: the whole card is already a link. */}
        {description && (
          <p className={cx(styles.fieldValue, preLine)}>
            {shrink(description, 200)}
          </p>
        )}
        {engagement && (
          <div className={styles.reactionBar}>
            {engagement.commentsEnabled && engagement.commentCount > 0 && (
              <p>
                {pluralize(engagement.commentCount, "Kommentar", "Kommentare")}
              </p>
            )}

            {engagement.visitCounts > 0 && (
              <p>{pluralize(engagement.visitCounts, "Besuch", "Besuche")}</p>
            )}
          </div>
        )}
      </div>
    </CardBox>
  );
};

type ShowroomEntryPreviewProps = {
  entry: ShowroomEntryListItem;
  unseenCount?: number;
  workshopSettings: PreviewSettings;
};

/**
 * A stored entry's card in the showroom, linking to its team's full entry.
 */
export const ShowroomEntryPreview = ({
  entry,
  unseenCount = 0,
  workshopSettings,
}: ShowroomEntryPreviewProps) => {
  const { upload, coverImage } = entry.formData as PreviewFormData;

  return (
    <ShowroomEntryCard
      formData={entry.formData}
      memberNames={entry.teamMembers.map((u) => u.name)}
      coverPath={
        coverImage?.path ||
        coverImage?.data?.path ||
        (upload?.data?.probe?.width ? upload.data.path : null)
      }
      to={entry.smallTeamId}
      engagement={{
        unseenCount,
        reactionCounts: entry.reactionCounts,
        commentCount: entry.commentCount,
        visitCounts: entry.visitCounts,
        commentsEnabled: workshopSettings.commentsEnabled,
      }}
    />
  );
};

/**
 * The showroom's masonry of entry cards. Each card gets a slot of its own to
 * carry the masonry's spacing — see `gridItem`.
 */
export const ShowroomEntryGrid = ({ children }: { children: ReactNode }) => (
  <div className={styles.grid}>
    {Children.map(children, (child) => (
      <div className={styles.gridItem}>{child}</div>
    ))}
  </div>
);
